import { useEffect, useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useRouter } from 'next/compat/router'
import { UseFormMethods } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { formatISO, startOfDay } from 'date-fns'

import { currentDateRounded } from '@/components/ui'
import {
  useAiOperatorFieldsQuery,
  useAssociateEmailRecipientsMutation,
  useCreateRuleMutation,
  useDevicesAlarmQuery,
  useDevicesCameraQuery,
  useDevicesDoorQuery,
  useFacilityTypesQuery,
  useFloorsByFacilityIdsQuery,
  useManualIncidentCategoriesQuery,
  useRuleRecipientsQuery,
  useUpdateAiOperatorResponseRulesMutation,
} from '@/graphql/generated/hooks'
import {
  Action,
  AiOperatorActionType,
  AiOperatorFieldType,
  AiOperatorResponseRuleInput,
  CreateRuleInput,
  DeviceType,
  FacilityType,
  RepeatInterval,
  RuleEventType,
  StreamingType,
} from '@/graphql/generated/schemas'
import { getLocalTimeString } from '@/utils/formatDateTime'
import {
  CreatableOptionI,
  OptionI,
  getIncidentFacilityOptions,
  getRecipientsEmail,
} from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'
import { INCIDENT_RULES } from '@/utils/routes'

import { Criterion } from '../ai-operator-criteria/types/types'
import {
  AI_OPERATOR_DOOR_EVENT_TYPES,
  getAiOperatorFieldOptions,
} from '../ai-operator-criteria/utils/utils'
import { DayOptionI, FormInputsT } from '../types/types'
import {
  DAYS,
  adjustEndTimeForCrossDay,
  getAlarmsOptions,
  getCamerasOptions,
  getDevicesByFacilityIdsOptions,
  getEventOptions,
  getFacilityTimeZone,
  getFloorByFacilityIdsOptions,
  getManualCategoryIds,
  getOptionValues,
  getRuleEmails,
  getRuleLogicInput,
  getSelectedDaysAsString,
  mapRuleEventsInput,
  setValidDevicesForFloorsSelected,
} from '../utils/utils'

export type UseCreateIncidentRulesIProps = {
  form: UseFormMethods<FormInputsT>
  criteria?: Criterion[]
  isActionAiOperator?: boolean
}

export const useCreateIncidentRules = ({
  form,
  criteria,
  isActionAiOperator,
}: UseCreateIncidentRulesIProps) => {
  const { getValues, reset, watch } = form
  const { push } = useRouter()
  const { showSuccess, showError } = useToasts()
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const [shouldIncludeAllFloors, setShouldIncludeAllFloors] = useState(true)
  const [shouldIncludeAllEvents, setShouldIncludeAllEvents] = useState(true)
  const [shouldIncludeAllCameras, setShouldIncludeAllCameras] = useState(null)
  const [shouldIncludeAllDoors, setShouldIncludeAllDoors] = useState(null)
  const [shouldIncludeAllAlarms, setShouldIncludeAllAlarms] = useState(null)
  const [shouldRepeat, setShouldRepeat] = useState(false)
  const [shouldShowDays, setShouldShowDays] = useState(false)
  const [shouldEnd, setShouldEnd] = useState(false)
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(null)
  const [selectedFacilityTimeZone, setSelectedFacilityTimeZone] =
    useState<string>(null)
  const [selectedFloorIds, setSelectedFloorIds] = useState<string[]>([])
  const [numberOfRepeats, setNumberOfRepeats] = useState(1)
  const [days, setDays] = useState<DayOptionI[]>(DAYS)
  const [selectedStartDate, setSelectedStartDate] =
    useState<Date>(currentDateRounded)
  const [selectedEndTime, setSelectedEndTime] = useState<Date>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(null)
  const [isAllDay, setIsAllDay] = useState(false)
  const watchAction = watch('action')
  const watchEventOptions = watch('event')

  const { isImplementer } = usePermissions()
  const isAiOperatorEnabled = useFeatureFlag(FeatureFlag.aiOperator)
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const isDevelopmentModeEnabled = useFeatureFlag(FeatureFlag.development)
  const [isDryRun, setIsDryRun] = useState(false)

  const onDryRunToggle = () => {
    setIsDryRun((prevIsDryRun) => !prevIsDryRun)
  }

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityTypesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          type: FacilityType.Normal,
          ...(debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : {}),
        },
      },
    })

  const { data: floorsData, loading: isFloorsLoading } =
    useFloorsByFacilityIdsQuery({
      fetchPolicy: 'network-only',
      variables: { facilityIds: [selectedFacilityId] },
      skip: !selectedFacilityId,
    })

  const { data: doorsData, loading: isDoorsLoading } = useDevicesDoorQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        facilityId: selectedFacilityId,
        floorIdIn: selectedFloorIds,
        type: DeviceType.Door,
      },
    },
    skip: !selectedFacilityId,
  })

  const { data: camerasData, loading: isCamerasLoading } =
    useDevicesCameraQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          facilityId: selectedFacilityId,
          floorIdIn: selectedFloorIds,
          type: DeviceType.Camera,
          streamingTypeIn: [
            StreamingType.AvaCloud,
            StreamingType.VerkadaCloud,
            StreamingType.LumanaCloud,
          ],
        },
      },
      skip: !selectedFacilityId,
    })

  const { data: alarmsData, loading: isAlarmsLoading } = useDevicesAlarmQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        facilityId: selectedFacilityId,
        floorIdIn: selectedFloorIds,
        type: DeviceType.Alarm,
      },
    },
    skip: !selectedFacilityId,
  })

  const { data: ruleRecipientsData, loading: isRecipientsLoading } =
    useRuleRecipientsQuery({
      skip: getValues().action?.value !== Action.SendEmail,
    })
  const { data: categoriesData } = useManualIncidentCategoriesQuery()

  const { data: aiOperatorFieldsData, loading: isAiOperatorFieldsLoading } =
    useAiOperatorFieldsQuery({
      variables: {
        filter: {
          enabled: true,
          type: AiOperatorFieldType.BooleanLabel,
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !isActionAiOperator,
    })

  const [createRuleMutation, { loading: isLoading }] = useCreateRuleMutation()
  const [associateEmailRecipients] = useAssociateEmailRecipientsMutation()
  const [updateAiOperatorRuleMutation, { loading: isAiOperatorRulesLoading }] =
    useUpdateAiOperatorResponseRulesMutation()

  const facilityOptions = getIncidentFacilityOptions(facilitiesData)
  const floorOptions = getFloorByFacilityIdsOptions(floorsData)
  const doorOptions = getDevicesByFacilityIdsOptions(doorsData)
  const cameraOptions = getCamerasOptions(camerasData)
  const alarmOptions = getAlarmsOptions(alarmsData)
  const timeZone = selectedFacilityTimeZone

  const emailOptions = useMemo(
    () => getRuleEmails(ruleRecipientsData),
    [ruleRecipientsData]
  )

  const updateSelectedFacilityId = (option: OptionI) => {
    const facilityId = option?.value
    setSelectedFacilityId(facilityId)

    // Calculate and cache timezone when facility is selected
    if (facilityId && facilitiesData) {
      const timeZone = getFacilityTimeZone(facilitiesData, facilityId)
      setSelectedFacilityTimeZone(timeZone)
    } else {
      setSelectedFacilityTimeZone(null)
    }

    // Reset floor+device options when the facility is changed
    reset({
      ...getValues(),
      floor: null,
      doors: null,
      cameras: null,
      alarms: null,
    })
  }

  const isActionOptionDisabled = (action: OptionI<Action>) =>
    !isAiOperatorEnabled && action.value === Action.EnableAiOperator

  // Floors
  const updateToggleAllFloors = (
    isToggleAll: boolean,
    selectedFloorOptions: OptionI[]
  ) => {
    setShouldIncludeAllFloors(isToggleAll)
    const options = isToggleAll ? floorOptions : selectedFloorOptions
    updateSelectedFloorIds(options)
  }

  const updateSelectedFloorIds = (options: OptionI[]) => {
    const floorIds = getOptionValues(options)
    setSelectedFloorIds(floorIds)
    if (options?.length <= 0) {
      reset({
        ...getValues(),
        doors: null,
        cameras: null,
        alarms: null,
      })
    }
  }

  const updateNumberOfRepeats = (
    _valueAsString: string,
    valueAsNumber: number
  ) => {
    setNumberOfRepeats(valueAsNumber)
  }

  const updateSelectedDays = (value: string) => {
    const updatedDays = days.map((d) => {
      if (value === d.value) {
        return { ...d, selected: !d.selected }
      }
      return d
    })
    setDays(updatedDays)
  }

  const updateAiOperatorResponseRules = async (
    ruleId: string
  ): Promise<boolean> => {
    try {
      if (!criteria?.length) {
        throw new Error('No criteria provided for AI Operator rules')
      }

      const responseRules: AiOperatorResponseRuleInput[] = criteria?.map(
        (c) => ({
          action: c.action,
          detected: c.detected,
          fieldId: c?.criterion.id,
          ...(isImplementer &&
            c?.aiOperatorModelId && { modelId: c.aiOperatorModelId }),
        })
      )
      await updateAiOperatorRuleMutation({
        variables: {
          input: {
            //The aiOperatorDefaultAction defines the default action that the AI operator system
            //takes when evaluating an incident rule but finds no specific matching response rules
            isDryRun,
            aiOperatorDefaultAction: AiOperatorActionType.FlagAndRelease,
            aiOperatorResponseRules: responseRules,
            ruleId,
          },
        },
      })
      return true
    } catch (e) {
      showError(e?.message)
      return false
    }
  }

  const updateEndTime = (endTime: Date) => {
    const adjustedEndTime = adjustEndTimeForCrossDay(selectedStartDate, endTime)
    setSelectedEndTime(adjustedEndTime)
  }

  const updateDate = (date: Date) => {
    setSelectedStartDate(date)

    if (selectedEndTime) {
      const adjustedEndTime = adjustEndTimeForCrossDay(date, selectedEndTime)
      setSelectedEndTime(adjustedEndTime)
    }
  }

  const getDeviceIds = (formValues: FormInputsT) => {
    const doors = shouldIncludeAllDoors
      ? getOptionValues(doorOptions)
      : getOptionValues(formValues?.doors ?? [])
    const cameras = shouldIncludeAllCameras
      ? getOptionValues(cameraOptions)
      : getOptionValues(formValues?.cameras ?? [])
    const alarms = shouldIncludeAllAlarms
      ? getOptionValues(alarmOptions)
      : getOptionValues(formValues?.alarms ?? [])
    return [...doors, ...cameras, ...alarms]
  }

  const createRule = async (formValues: FormInputsT) => {
    const action = formValues.action.value
    const manualIncidentCategoryIds = getManualCategoryIds(
      formValues?.event,
      categoriesData
    )
    const hasManualCategories = manualIncidentCategoryIds?.length > 0
    const events = shouldIncludeAllEvents
      ? [RuleEventType.All]
      : mapRuleEventsInput(formValues?.event, categoriesData)
    const deviceIds = getDeviceIds(formValues)
    const repeatType: RepeatInterval = formValues.repeatType?.value
    const shouldReapeatWeekly =
      shouldRepeat && repeatType === RepeatInterval.Weekly

    const createRuleInput: CreateRuleInput = {
      action,
      name: formValues?.name,
      ruleEventType: events,
      ruleLogics: getRuleLogicInput(deviceIds),
      // Sent as raw local wall-clock digits, no timezone conversion at all -- a
      // timezone is applied exactly once, at evaluation time, using the
      // incident's own originating facility. See ENG-30856.
      startTime: isAllDay
        ? getLocalTimeString(startOfDay(selectedStartDate))
        : getLocalTimeString(selectedStartDate),
      allDayOverride: isAllDay,
      // Optional inputs
      ...(hasManualCategories && {
        // Add ids of all Manual events
        manualIncidentCategoryIds: manualIncidentCategoryIds,
      }),
      ...(!isAllDay && {
        endTimeOfDay: getLocalTimeString(
          adjustEndTimeForCrossDay(selectedStartDate, selectedEndTime)
        ),
      }),
      ...(shouldReapeatWeekly && { day: getSelectedDaysAsString(days) }),
      ...(shouldRepeat && { repeatInterval: repeatType }),
      ...(shouldRepeat && { repeatIntervalNum: numberOfRepeats }),
      ...(shouldEnd && {
        endDate: formatISO(selectedEndDate, { representation: 'date' }),
      }),
    }

    try {
      const { data } = await createRuleMutation({
        variables: {
          input: createRuleInput,
        },
      })
      const ruleId = data?.createRule?.rule?.id
      if (action === Action.SendEmail) {
        await associateRecipients(formValues.emails, ruleId)
      }
      // Handle AI Operator rules if needed
      if (ruleId && isActionAiOperator) {
        await updateAiOperatorResponseRules(ruleId)
      }
      showSuccess('Successfully created Rule')
      push(INCIDENT_RULES)
    } catch (e) {
      showError(e?.message)
    }
  }

  const associateRecipients = async (
    options: CreatableOptionI[],
    ruleId: string
  ) => {
    const emailRecipients = getRecipientsEmail(options)
    await associateEmailRecipients({
      variables: { input: { ruleId, emailRecipients } },
    })
  }

  const eventOptions = useMemo(
    () => getEventOptions(watchAction?.value, categoriesData),
    [categoriesData, watchAction?.value]
  )

  const selectedAiOperatorEventTypes = useMemo(
    () =>
      watchEventOptions
        ?.map((eventOption) => eventOption?.value)
        ?.filter((eventType) => AI_OPERATOR_DOOR_EVENT_TYPES.has(eventType)) ||
      [],
    [watchEventOptions]
  )

  const criteriaOptions: Criterion[] = useMemo(() => {
    if (
      aiOperatorFieldsData &&
      !isAiOperatorFieldsLoading &&
      isActionAiOperator
    ) {
      return getAiOperatorFieldOptions(
        aiOperatorFieldsData,
        selectedAiOperatorEventTypes,
        isTraceLogsEnabled
      )
    }
    return []
  }, [
    aiOperatorFieldsData,
    isAiOperatorFieldsLoading,
    isActionAiOperator,
    selectedAiOperatorEventTypes,
    isTraceLogsEnabled,
  ])

  useEffect(() => {
    // Refresh start time to current time when component mounts to avoid stale TimePicker values
    setSelectedStartDate(currentDateRounded)
  }, [])

  useEffect(() => {
    // Validate that device option is on selected floor
    if (doorsData && camerasData && alarmsData) {
      setValidDevicesForFloorsSelected(form, doorsData, camerasData, alarmsData)
    }
  }, [doorsData, camerasData, alarmsData])

  return {
    eventOptions,
    shouldIncludeAllFloors,
    shouldIncludeAllCameras,
    shouldIncludeAllDoors,
    shouldIncludeAllAlarms,
    shouldIncludeAllEvents,
    shouldRepeat,
    shouldShowDays,
    shouldEnd,
    numberOfRepeats,
    days,
    selectedStartDate,
    selectedEndDate,
    selectedEndTime,
    timeZone,
    setShouldIncludeAllEvents,
    setShouldIncludeAllCameras,
    setShouldIncludeAllDoors,
    setShouldIncludeAllAlarms,
    setSelectedStartDate,
    updateEndTime,
    updateDate,
    setSelectedEndDate,
    setSelectedEndTime,
    setShouldRepeat,
    setShouldShowDays,
    setShouldEnd,
    setFacilityName,
    updateSelectedFacilityId,
    updateToggleAllFloors,
    updateSelectedFloorIds,
    updateNumberOfRepeats,
    updateSelectedDays,
    createRule,
    isActionOptionDisabled,
    isDevelopmentModeEnabled,
    facilityOptions,
    floorOptions,
    cameraOptions,
    doorOptions,
    alarmOptions,
    emailOptions,
    criteriaOptions,
    isFacilitiesLoading,
    isFloorsLoading,
    isRecipientsLoading,
    isCamerasLoading,
    isDoorsLoading,
    isAlarmsLoading,
    isAiOperatorFieldsLoading,
    isLoading: isLoading || isAiOperatorRulesLoading,
    toggleAllDaySwitch: setIsAllDay,
    isAllDay,
    isDryRun,
    onDryRunToggle,
  }
}
